import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Globe2, Heart, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { IMAGES } from "@/constants/site";

const values = [
  { icon: Globe2, t: "Places over profiles", d: "We connect people through places and real-life moments — not followers or algorithms." },
  { icon: Heart, t: "Real connection", d: "Discover the world through the eyes of the people who actually live there." },
  { icon: ShieldCheck, t: "Safety-first", d: "A verified community with privacy and safety controls built in from day one." },
  { icon: Sparkles, t: "Creativity, amplified", d: "From live rooms to the AI Music Studio, we give creators new ways to be seen and heard." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="px-6 pb-8 pt-32 md:px-12 md:pt-44">
        <div className="mx-auto max-w-5xl">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-bold uppercase tracking-[0.2em] text-aura-pink">
            About AuraLive
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mt-5 max-w-4xl font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-aura-ink sm:text-5xl sm:tracking-tighter md:text-7xl">
            Connecting humanity, <span className="grad-text">one live moment</span> at a time.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-6 max-w-2xl text-lg leading-relaxed text-aura-text2">
            AuraLive is a real-time streaming platform that connects people
            through places. Explore life live from anywhere on Earth, and see the
            world through someone else's eyes.
          </motion.p>
        </div>
      </section>

      <section className="px-6 pb-8 md:px-12">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl border border-black/[0.06]">
          <img src={IMAGES.social} alt="People connecting on AuraLive" className="h-64 w-full object-cover md:h-[420px]" />
        </div>
      </section>

      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-black/[0.06] bg-white p-8 solid-card md:p-10">
            <h2 className="font-heading text-3xl font-medium tracking-tight text-aura-ink">Our vision</h2>
            <p className="mt-4 leading-relaxed text-aura-text2">
              Technology should bring people closer together — not divide them.
              Our vision is to build the world's most trusted real-time live
              platform, where people safely explore cultures, communities and
              places while building understanding across borders.
            </p>
          </div>
          <div className="rounded-3xl border border-black/[0.06] bg-white p-8 solid-card md:p-10">
            <h2 className="font-heading text-3xl font-medium tracking-tight text-aura-ink">Our mission</h2>
            <p className="mt-4 leading-relaxed text-aura-text2">
              To connect humanity through places — making real-time experiences
              safer, smarter and accessible to everyone, so understanding begins
              with seeing life through someone else's eyes.
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24 md:px-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="max-w-xl font-heading text-3xl font-medium tracking-tight text-aura-ink md:text-4xl">
            What we stand for.
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {values.map((v) => (
              <div key={v.t} className="rounded-3xl border border-black/[0.06] bg-white p-8 solid-card">
                <span className="grid h-11 w-11 place-items-center rounded-2xl grad-bg text-white">
                  <v.icon size={20} />
                </span>
                <h3 className="mt-5 font-heading text-xl font-medium tracking-tight text-aura-ink">{v.t}</h3>
                <p className="mt-2 leading-relaxed text-aura-text2">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-28 md:px-12">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 rounded-[2rem] border border-black/[0.06] bg-white p-10 text-center solid-card md:p-14">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-aura-ink md:text-4xl">
            Ready to explore the world live?
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/#download" className="inline-flex items-center gap-2 rounded-full bg-aura-ink px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-aura-ink2">
              Download app <ArrowRight size={16} />
            </Link>
            <Link to="/safety" className="inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-3.5 text-sm font-semibold text-aura-ink transition-colors hover:border-black/40">
              Our safety approach
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
